import {RouteObject, useMatches} from 'react-router-dom'
import {StyledBreadCrumbs, StyledHeaderBreadcrumbContainer} from '@components/commons/bread-crumbs/style.ts'
import {ButtonNavLink} from '@components/ui/button-nav-link/ButtonNavLink.tsx'
import {ChevronRightIcon} from '@components/ui/icon/Icon.tsx'
import {Fragment, ReactNode} from 'react'
import {useTranslation, TKey} from '@/translations/i18n.tsx'
import {useBreadcrumbEntityName} from '@/hooks/useBreadcrumbEntityName'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'

export type HandleRouterObj = {
    path: string
    paramName?: string
    name?: string
}

type Crumb = {
    item: HandleRouterObj
    match: RouteObject
}

// Separate component for each breadcrumb item to properly use hooks
const BreadcrumbItem = ({item, match, isLast}: {item: HandleRouterObj; match: RouteObject; isLast: boolean}) => {
    const {t} = useTranslation()

    const path = !item?.paramName
        ? item.path
        : // @ts-expect-error [TS2339] Necessary because params is not mapped in the RouteObject Type
          item.path.replace(`:${item.paramName}`, match?.params[item.paramName])

    // @ts-expect-error [TS2339] Necessary because params is not mapped in the RouteObject Type
    const entityId = item?.paramName ? match?.params[item.paramName] : undefined

    // Use the hook to get entity name
    const {name: entityName, isLoading} = useBreadcrumbEntityName({
        routePath: item.path,
        entityId
    })

    // Determine the display name
    let displayName: string | ReactNode

    if (item?.name) {
        displayName = t(item.name as TKey)
    } else if (entityName) {
        displayName = String(entityName)
    } else if (entityId) {
        displayName = entityId
    } else {
        displayName = ''
    }

    return (
        <Fragment>
            {isLoading ? (
                <Skeleton width={100} height={20} />
            ) : (
                <ButtonNavLink to={path} variant="ghost" shape="text" size={'sm'}>
                    <span>{displayName}</span>
                </ButtonNavLink>
            )}
            {!isLast && <ChevronRightIcon data-color size={16} />}
        </Fragment>
    )
}

export const BreadCrumbs = () => {
    const matches: RouteObject[] = useMatches()
    const crumbs = matches
        // first get rid of any matches that don't have handle and crumb
        .filter(match => Boolean(match.handle))
        // now map them into an array of elements, passing the loader
        // data to each one
        .map((match): Crumb => ({item: match.handle, match: match}))

    return (
        <StyledHeaderBreadcrumbContainer>
            <StyledBreadCrumbs align="center" gap={1}>
                {crumbs.map(({item, match}, index) => (
                    <BreadcrumbItem
                        key={`${item.path}-${index}`}
                        item={item}
                        match={match}
                        isLast={index === crumbs.length - 1}
                    />
                ))}
            </StyledBreadCrumbs>
        </StyledHeaderBreadcrumbContainer>
    )
}

BreadCrumbs.displayName = 'BreadCrumbs'
